<script setup>
import 'swiper/css';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-fullscreen.css';
import 'lightgallery/css/lg-video.css';
import { Swiper } from 'swiper';
import { Navigation, A11y, Manipulation } from 'swiper/modules';
import lgModule from 'lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgFullscreen from 'lightgallery/plugins/fullscreen';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';
import ArrowButton from '~/components/common/ArrowButton.vue';
import ProductGalleryImage from './ProductGalleryImage.vue';
import ProductGalleryVideo from './ProductGalleryVideo.vue';

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },

  injectedVideo: {
    type: Object,
    default: () => ({}),
  },
});

const slideTo = (index) => {
  if (swiperInstance.value && index >= 0) {
    swiperInstance.value.slideToLoop(index);
    return index;
  }
  return -1;
};

const emit = defineEmits(['update:index']);
defineExpose({ slideTo });

const lightGallery = ref(null);
const lgContainer = ref(null);
const swiperInstance = ref(null);
const activeSlideIndex = ref(0);
const galleryInstance = ref(null);
const arrowTopPosition = ref('50%');
let resizeHandler = null;

const images = computed(() => {
  const productImages = props.product.images.edges.map((image) => {
    return {
      id: image.node.id,
      src: image.node.src,
      alt: image.node.altText || props.product.title,
    };
  });
  // push into second position if video is available
  if (props.injectedVideo?.length > 0) {
    // only take first video
    const video = props.injectedVideo[0];

    productImages.splice(1, 0, {
      id: video.id || 'video',
      src: video.url,
      alt: video.title || 'Product Video',
      type: 'video',
    });
  }

  return productImages;
});

onMounted(async () => {
  if (images.value.length) {
    Swiper.use([Navigation, A11y, Manipulation]);

    swiperInstance.value = new Swiper('.swiper', {
      slidesPerView: 1,
      spaceBetween: 10,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      grabCursor: true,
      loop: true,
      on: {
        slideChange: (swiper) => {
          activeSlideIndex.value = swiper.realIndex;
        },
      },
    });

    const firstImage = document.querySelector('.swiper-slide img');
    if (firstImage) {
      resizeHandler = () => {
        const imageHeight = firstImage.offsetHeight;
        arrowTopPosition.value = `${imageHeight / 2}px`;
      };

      if (firstImage.complete) {
        resizeHandler();
      } else {
        firstImage.addEventListener('load', resizeHandler);
      }

      window.addEventListener('resize', resizeHandler);
    }

    // Initialize LightGallery
    if (lgContainer.value) {
      lightGallery.value = lgModule(lgContainer.value, {
        licenseKey: '57BD9E3E-414E-415B-B0D4-6860474F6F93',
        plugins: [lgFullscreen, lgZoom, lgThumbnail, lgVideo],
        speed: 200,
        closable: true,
        loop: true,
        escKey: true,
        keyPress: true,
        counter: true,
        enableDrag: true,
        enableSwipe: true,
        thumbnail: false,
        animateThumb: true,
        showThumbByDefault: false,
        zoomFromOrigin: true,
        allowMediaOverlap: false,
        toggleThumb: true,
        download: false,
        fullScreen: true,
        controls: true,
        dynamic: true,
        dynamicEl: images.value.map((img) => {
          if (img.type === 'video') {
            return {
              video: {
                source: [{ src: img.src, type: 'video/mp4' }],
                attributes: { preload: 'metadata', controls: true, loop: true },
              },
              subHtml: `<p>${img.alt}</p>`,
            };
          }
          return {
            src: img.src,
            subHtml: `<p>${img.alt}</p>`,
          };
        }),
        autoplayFirstVideo: false,
        autoplayVideoOnSlide: false,
        mobileSettings: {
          controls: true,
          showCloseIcon: true,
          fullScreen: true,
          download: false,
        },
        // Accessibility enhancements
        ariaLabelledby: 'gallery-heading',
        ariaDescribedby: 'gallery-description',
      });
    }

    galleryInstance.value = {
      swiper: swiperInstance.value,
      lightGallery: lightGallery.value,
      lgContainer: lgContainer.value,
    };
    window.productGallery = galleryInstance.value;
  }
});

onUnmounted(() => {
  if (window.productGallery === galleryInstance.value) {
    delete window.productGallery;
  }

  // Cleanup resize listener
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
  }
});

const imageMetadata = computed(() => {
  return props.product?.imageMetadata ? props.product.imageMetadata : [];
});

provide('lightGalleryInstance', lightGallery);
</script>

<template>
  <figure class="w-full" aria-labelledby="gallery-heading" aria-describedby="gallery-description">
    <h2 id="gallery-heading" class="sr-only">{{ $t('gallery.product-images') }}</h2>
    <p id="gallery-description" class="sr-only">
      {{ $t('gallery.product-image-description') }}
    </p>

    <div class="swiper relative">
      <div class="absolute -translate-y-1/2 left-2 z-10" :style="{ top: arrowTopPosition }">
        <ArrowButton
          class="swiper-button-prev size-11 flex items-center justify-center rounded-full bg-white/30 hover:bg-white/50"
          @click="swiperInstance?.slidePrev()"
          as-child
          direction="left"
        />
      </div>
      <div class="absolute -translate-y-1/2 right-2 z-10" :style="{ top: arrowTopPosition }">
        <ArrowButton
          class="swiper-button-next size-11 flex items-center justify-center rounded-full bg-white/30 hover:bg-white/50"
          @click="swiperInstance?.slideNext()"
          as-child
          direction="right"
        />
      </div>

      <div class="swiper-wrapper" ref="lgContainer">
        <template v-for="(image, index) in images" :key="index">
          <component
            :is="image.type === 'video' ? ProductGalleryVideo : ProductGalleryImage"
            v-bind="{
              image,
              imageMetadata,
              index,
              product,
              activeSlideIndex,
            }"
          />
        </template>
      </div>
    </div>

    <div v-if="images.length === 0" class="flex items-center justify-center w-full h-72 bg-gray-200 text-gray-800">
      <span>{{ $t('gallery.no-images') }}</span>
    </div>
  </figure>
</template>
